import { Component, Input, OnInit } from '@angular/core';
import { DataAnalysisApiService } from '../../services/api/data-analysis-api.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { Color, ScaleType } from '@swimlane/ngx-charts';

@Component({
  selector: 'app-data-analysis-sale',
  templateUrl: './data-analysis-sale.component.html',
  styleUrl: './data-analysis-sale.component.css'
})
export class DataAnalysisSaleComponent implements OnInit {

  @Input() branchMode = false;

  channels: any[] = [];

  saleDatasets: { name: string, series: { name: string, value: number }[] }[] = [];

  formattedData: { date: string, channel: string, total_order: number, total_amount: number, subtotal: number, total_discount: number }[] = []

  selectedDate = { type: 'daypass', value: '0' };
  selectedChannel: string[] = [];
  readonly ALL_CHANNEL_MARKER = '__all__';

  summary = {
    totalOrder: 0,
    subtotal: 0,
    totalDiscount: 0,
    totalAmount: 0,
    avgAmount: 0
  }

  displayData: 'total_order' | 'total_amount' | 'total_discount' = 'total_order';

  colorScheme: Color = {
    domain: [],
    group: ScaleType.Ordinal,
    selectable: true,
    name: 'platform color'
  }

  loading = false

  constructor(
    private utilService: UtilService,
    private dataAnalysisApiService: DataAnalysisApiService,
    private platformService: PlatformService
  ) { }

  ngOnInit(): void {
    this.channels = (this.platformService.channels || []).filter(c => !!c.is_branch === this.branchMode);
    this.getDataReport();
  }

  async getDataReport() {

    if (this.loading) return;

    this.loading = true;

    let filters: any = {}
    let activeChannels: any[];
    if (this.selectedChannel?.length > 0) {
      filters['channel'] = this.selectedChannel.toString()
      activeChannels = this.channels.filter(c => this.selectedChannel.includes(c.shop_ref));
    } else {
      filters['channel'] = this.channels.map((item) => item.shop_ref).toString()
      activeChannels = this.channels;
    }

    const branchChannels = activeChannels.filter(c => c.is_branch);
    const regularChannels = activeChannels.filter(c => !c.is_branch);
    const getByDate = async (f: any) => {
      const calls: Array<Promise<any>> = [];
      if (regularChannels.length > 0) {
        calls.push(this.dataAnalysisApiService.getSaleReportByDate({
          ...f,
          channel: regularChannels.map(c => c.shop_ref).toString()
        }));
      }
      if (branchChannels.length > 0) {
        calls.push(this.dataAnalysisApiService.getSaleReportBranchByDate({
          ...f,
          channel: branchChannels.map(c => c.shop_ref).toString()
        }));
      }
      const results = await Promise.all(calls);
      return { data: results.flatMap((r: any) => r?.data || []) };
    };

    try {
      if (this.selectedDate.type === 'daypass') {

        if (this.selectedDate.value === '0') {
          const date = this.utilService.dayjs().format('YYYY-MM-DD')
          filters = { ...filters, date }
          const result: any = await getByDate(filters)
          this.formattedData = result.data.map((item: any) => ({ ...item, date: item.time_period }));
          this.saleDatasets = this.formatData(this.formattedData)

        } else if (this.selectedDate.value === '1') {
          const date = this.utilService.dayjs().subtract(parseInt(this.selectedDate.value), 'day').format('YYYY-MM-DD')
          filters = { ...filters, date }
          const result: any = await getByDate(filters)
          this.formattedData = result.data.map((item: any) => ({ ...item, date: item.time_period }));
          this.saleDatasets = this.formatData(this.formattedData)

        } else if (this.selectedDate.value === 'month') {
          const startDate = this.utilService.dayjs().startOf('month').format('YYYY-MM-DD')
          const endDate = this.utilService.dayjs().format('YYYY-MM-DD')
          if (startDate === endDate) {
            filters = { ...filters, date: startDate }
            const result: any = await getByDate(filters)
            this.formattedData = result.data.map((item: any) => ({ ...item, date: item.time_period }));
            this.saleDatasets = this.formatData(this.formattedData)
          } else {
            const dateRange = [startDate, endDate].toString()
            filters = { ...filters, date: dateRange }
            const result: any = await this.dataAnalysisApiService.getSaleReportByDateRange(filters)
            this.formattedData = result.data.map((item: any) => ({ ...item, date: item.date }));
            this.saleDatasets = this.formatData(this.formattedData)
          }

        } else {
          const startDate = this.utilService.dayjs().subtract(parseInt(this.selectedDate.value), 'day').format('YYYY-MM-DD')
          const endDate = this.utilService.dayjs().subtract(1, 'day').format('YYYY-MM-DD')
          const dateRange = [startDate, endDate].toString()
          filters = { ...filters, date: dateRange }
          const result: any = await this.dataAnalysisApiService.getSaleReportByDateRange(filters)
          this.formattedData = result.data.map((item: any) => ({ ...item, date: item.date }));
          this.saleDatasets = this.formatData(this.formattedData)
        }
      }
      if (this.selectedDate.type === 'date-range') {
        const startDate = this.utilService.dayjs(this.selectedDate.value.split(',')[0]).format('YYYY-MM-DD')
        const endDate = this.utilService.dayjs(this.selectedDate.value.split(',')[1]).format('YYYY-MM-DD')

        if (startDate != endDate) {
          const dateRange = [startDate, endDate].toString()
          filters = { ...filters, date: dateRange }
          const result: any = await this.dataAnalysisApiService.getSaleReportByDateRange(filters)
          this.formattedData = result.data.map((item: any) => ({ ...item, date: item.date }));
          this.saleDatasets = this.formatData(this.formattedData)

        } else {
          const date = this.utilService.dayjs(startDate).format('YYYY-MM-DD')
          filters = { ...filters, date }
          const result: any = await getByDate(filters)
          this.formattedData = result.data.map((item: any) => ({ ...item, date: item.time_period }));
          this.saleDatasets = this.formatData(this.formattedData)
        }
      }
    } catch {
      this.formattedData = [];
      this.saleDatasets = [];
      this.summary = { totalOrder: 0, subtotal: 0, totalDiscount: 0, totalAmount: 0, avgAmount: 0 };
    } finally {
      this.loading = false;
    }
  }

  formatData(data: { date: string, channel: string, total_order: number, total_amount: number, subtotal: number, total_discount: number }[]) {

    this.summary = {
      totalOrder: 0,
      subtotal: 0,
      totalDiscount: 0,
      totalAmount: 0,
      avgAmount: 0
    }
    for (const item of data) {
      this.summary.totalOrder += item.total_order;
      this.summary.subtotal += item.subtotal;
      this.summary.totalDiscount += item.total_discount;
      this.summary.totalAmount += item.total_amount;
    }
    this.summary.totalOrder = Math.round(this.summary.totalOrder)
    this.summary.subtotal = parseFloat((this.summary.subtotal).toFixed(2))
    this.summary.totalDiscount = parseFloat((this.summary.totalDiscount).toFixed(2))
    this.summary.totalAmount = parseFloat((this.summary.totalAmount).toFixed(2))
    this.summary.avgAmount = this.summary.totalOrder
      ? parseFloat((this.summary.totalAmount / this.summary.totalOrder).toFixed(2))
      : 0

    const uniqueDate = [...new Set(data.map(item => item.date))].sort()
    const result = []

    const channels: string[] = this.selectedChannel?.length > 0 ? this.selectedChannel : this.channels.map(item => item.shop_ref)

    this.colorScheme.domain = []

    for (const channel of channels) {

      const reportChannel = data.filter(item => item.channel === channel)
      const channelDetail = this.channels.find(item => item.shop_ref === channel)
      if (!channelDetail) continue;
      this.colorScheme.domain.push(channelDetail.color || '#32436a')

      const series = []
      for (const date of uniqueDate) {
        const reportDate = reportChannel.find(item => item.date === date)
        series.push({
          name: date,
          value: reportDate ? (reportDate[this.displayData] || 0) : 0
        })
      }

      result.push({
        name: channelDetail.platform_name,
        series
      })
    }
    return result
  }

  handleOptionSelected(selectedDate: { type: string, value: string }) {
    this.selectedDate = selectedDate
  }

  get displayedSelection(): string[] {
    if (this.channels.length > 0 && this.selectedChannel.length === this.channels.length) {
      return [this.ALL_CHANNEL_MARKER, ...this.selectedChannel];
    }
    return this.selectedChannel;
  }

  onChannelSelectionChange(values: any[]) {
    const list = Array.isArray(values) ? values : [];
    const wasAllSelected = this.channels.length > 0
      && this.selectedChannel.length === this.channels.length;
    const newHasMarker = list.includes(this.ALL_CHANNEL_MARKER);

    if (newHasMarker !== wasAllSelected) {
      this.selectedChannel = newHasMarker
        ? this.channels.map(c => c.shop_ref)
        : [];
    } else {
      this.selectedChannel = list.filter((v: unknown): v is string =>
        typeof v === 'string' && v !== this.ALL_CHANNEL_MARKER
      );
    }
  }

  removeChannelSelected(channel: any) {
    this.selectedChannel = this.selectedChannel.filter((e: string) => e !== channel.shop_ref)
  }

  removeChannelSelectedAll() {
    this.selectedChannel = []
  }

  displayChannel() {
    return this.channels.filter(c => this.selectedChannel.includes(c.shop_ref))
  }

  displayDataOnchange(event: any) {
    this.displayData = event.value
    this.saleDatasets = this.formatData(this.formattedData)
  }
}
